import { useQuery } from '@tanstack/react-query';
import { formatAbsolute, TIMELINE_HISTORY_STALE_MS } from '@odo/shared-ui';
import { fetchTimeline } from '../../../../services/timeline';
import { CountTabLabel } from '../CountTabLabel';
import { NoteChip } from '../design-dx/HandBackBanner';
import { noteSectionLabel } from '../design-dx/handBackNotes';
import { CARD_TEXT_CLASS, PANEL_BODY_CLASS } from '../design-dx/VersionCard';
import {
    HAND_BACK_EVENT_TYPE,
    pastFeedbackRounds,
    type PastFeedbackRound
} from './pastFeedbackRounds';

const HAND_BACK_FETCH_LIMIT = 50;
// Same marker as the clarification thread's latest entry.
const LATEST_MARKER_CLASS = 'text-[10px] font-bold uppercase tracking-wider text-gray-400';
const STATE_CLASS = 'flex items-center justify-center h-full text-xs text-center px-2';

function usePastFeedback(patientId: string) {
    return useQuery({
        // Under 'patient-timeline' so invalidateCaseDetail refreshes it after every move.
        queryKey: ['patient-timeline', patientId, HAND_BACK_EVENT_TYPE],
        queryFn: async () => {
            const res = await fetchTimeline(patientId, {
                type: HAND_BACK_EVENT_TYPE,
                limit: HAND_BACK_FETCH_LIMIT
            });
            return { total: res.total, rounds: pastFeedbackRounds(res.data, res.total) };
        },
        staleTime: TIMELINE_HISTORY_STALE_MS,
        enabled: Boolean(patientId)
    });
}

export function PastFeedbackTabLabel({ patientId }: { patientId: string }) {
    const { data } = usePastFeedback(patientId);
    return <CountTabLabel label="Past feedback" count={data ? data.total : null} />;
}

interface RoundItemProps {
    round: PastFeedbackRound;
    isLatest: boolean;
    isLast: boolean;
}

// Rail geometry matches the patient timeline's JourneyView stations.
function RoundItem({ round, isLatest, isLast }: RoundItemProps) {
    return (
        <li className={`relative pl-7 ${isLast ? '' : 'pb-5'}`}>
            {!isLast && (
                <span className="absolute left-[10px] top-4 -bottom-1 w-[2px] bg-gray-200" />
            )}
            <span className="absolute left-[5px] top-1 w-3 h-3 rounded-full border-2 border-gray-300 bg-white" />
            <div className="flex items-baseline gap-x-2 gap-y-0.5 flex-wrap">
                <span className="text-xs font-semibold text-gray-900">Round {round.round}</span>
                {isLatest && <span className={LATEST_MARKER_CLASS}>Latest</span>}
                <span className="text-[11px] text-gray-500">
                    handed back by {round.author} · {formatAbsolute(round.at)}
                </span>
            </div>
            <ul className="mt-2 flex flex-col gap-2">
                {round.notes.map((note, i) => (
                    <li key={`${note.section}-${i}`} className="flex items-baseline gap-2">
                        <NoteChip tone={note.section}>{noteSectionLabel(note.section)}</NoteChip>
                        <p
                            className={`${CARD_TEXT_CLASS} min-w-0 flex-1 whitespace-pre-wrap break-words`}
                        >
                            {note.text}
                        </p>
                    </li>
                ))}
            </ul>
        </li>
    );
}

export function PastFeedback({ patientId }: { patientId: string }) {
    const { data, isLoading, isError } = usePastFeedback(patientId);
    const rounds = data?.rounds;
    return (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden font-inter">
            <div className={PANEL_BODY_CLASS}>
                {isLoading ? (
                    <div className={`${STATE_CLASS} text-gray-400 italic`}>Loading feedback…</div>
                ) : isError ? (
                    <div className={`${STATE_CLASS} text-red-500`}>Failed to load feedback.</div>
                ) : !rounds || rounds.length === 0 ? (
                    <div className={`${STATE_CLASS} text-gray-400 italic`}>
                        No earlier hand-backs for this case.
                    </div>
                ) : (
                    <ol>
                        {rounds.map((round, i) => (
                            <RoundItem
                                key={round.id}
                                round={round}
                                isLatest={i === 0}
                                isLast={i === rounds.length - 1}
                            />
                        ))}
                    </ol>
                )}
            </div>
        </div>
    );
}
